import { type FC, useMemo, useState } from "react"

import CommunityEmptyState from "@forento/ui/components/CommunityEmptyState"
import useInfiniteScroll from "@forento/ui/hooks/useInfiniteScroll"

import LoadingIndicator from "~/components/LoadingIndicator"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { query } from "~/utilities/trpc"

import CategoryFilter from "./CategoryFilter"
import ChannelSelector from "./ChannelSelector"
import CreatePost from "./CreatePost"
import Post from "./Post"
import PostDetail from "./PostDetail"

const CommunityPosts: FC = () => {
	const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null)
	const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)
	const [selectedPostId, setSelectedPostId] = useState<number>()

	const channels = query.community.listChannels.useQuery()
	const categories = query.community.listPostCategories.useQuery()
	// Filter only by listed categories so the filter row can always clear the selection
	const activeCategoryId = categories.data?.some(x => x.id === selectedCategoryId) ? selectedCategoryId : null
	const posts = query.community.listPersonalizedPostsByChannel.useInfiniteQuery(
		{ channelId: selectedChannelId, categoryId: activeCategoryId },
		{ getNextPageParam: lastPage => lastPage.nextCursor },
	)

	const selectCategory = (id: number | null) => {
		setSelectedCategoryId(id)
		// A post can carry a category created after the list was loaded
		if (id !== null && !categories.data?.some(x => x.id === id)) void categories.refetch()
	}

	const items = useMemo(() => posts.data?.pages.flatMap(page => page.items) ?? [], [posts.data])

	const sentinelRef = useInfiniteScroll({
		hasNextPage: posts.hasNextPage,
		isFetchingNextPage: posts.isFetchingNextPage,
		fetchNextPage: posts.fetchNextPage,
	})

	if (channels.error || posts.error) return <p>Failed to load posts.</p>
	if (channels.data === undefined) return <PartialLoadingPage />

	const currentChannel = channels.data.find(channel => channel.id === selectedChannelId)!

	return (
		<div className="flex flex-col gap-6">
			{channels.data.length > 1 && (
				<ChannelSelector
					channels={channels.data}
					selectedChannelId={selectedChannelId}
					setSelectedChannelId={setSelectedChannelId}
				/>
			)}
			<CreatePost channel={currentChannel} onReloadPosts={posts.refetch} />
			{categories.data !== undefined && categories.data.length > 0 && (
				<CategoryFilter
					categories={categories.data}
					selectedCategoryId={activeCategoryId}
					onSelect={selectCategory}
				/>
			)}
			{posts.data === undefined ? (
				<PartialLoadingPage />
			) : (
				<div className="flex flex-col gap-4">
					{items.length === 0 && !posts.hasNextPage && (
						<CommunityEmptyState
							title="No posts yet"
							description={
								activeCategoryId !== null
									? "No posts in this category yet. Choose another category to see more posts."
									: "Every conversation starts with a first post. Share an update, ask a question, or say hello."
							}
						/>
					)}
					{items.map(post => (
						<Post
							key={post.id}
							post={post}
							isSelected={selectedPostId === post.id}
							onSelect={() => setSelectedPostId(post.id)}
							onChange={posts.refetch}
							onCategorySelect={selectCategory}
							allowPinning
						/>
					))}
					{posts.hasNextPage && (
						<div ref={sentinelRef} className="flex min-h-px justify-center py-5">
							{posts.isFetchingNextPage && <LoadingIndicator />}
						</div>
					)}
				</div>
			)}
			<PostDetail postId={selectedPostId ?? null} onClose={() => setSelectedPostId(undefined)} />
		</div>
	)
}

export const ProfileCommunityPosts: FC<{ profileId: number; gridArea: string }> = ({ profileId, gridArea }) => {
	const posts = query.community.listPersonalizedPostsByProfile.useInfiniteQuery(
		{ profileId },
		{ getNextPageParam: lastPage => lastPage.nextCursor },
	)

	const [selectedPostId, setSelectedPostId] = useState<number>()

	const items = useMemo(() => posts.data?.pages.flatMap(page => page.items) ?? [], [posts.data])

	const sentinelRef = useInfiniteScroll({
		hasNextPage: posts.hasNextPage,
		isFetchingNextPage: posts.isFetchingNextPage,
		fetchNextPage: posts.fetchNextPage,
	})

	if (posts.error) return <p style={{ gridArea }}>Failed to load posts.</p>
	if (posts.data === undefined) return <PartialLoadingPage style={{ gridArea }} />

	return (
		<div className="flex flex-col gap-6" style={{ gridArea }}>
			<div className="flex flex-col gap-4">
				{items.map(post => (
					<Post
						key={post.id}
						post={post}
						isSelected={selectedPostId === post.id}
						onSelect={() => setSelectedPostId(post.id)}
						onChange={posts.refetch}
						showChannel
					/>
				))}
				{posts.hasNextPage && (
					<div ref={sentinelRef} className="flex min-h-px justify-center py-5">
						{posts.isFetchingNextPage && <LoadingIndicator />}
					</div>
				)}
			</div>
			<PostDetail postId={selectedPostId ?? null} onClose={() => setSelectedPostId(undefined)} />
		</div>
	)
}

export default CommunityPosts
